import { FoaPart, ProcedureType } from "../../autogen/bff-api";
import type { AreaStatus } from "./creationAreas";
import type { ProcedureRule, ProcurementRules } from "./procurementRules";

export type RecommendationReason = "open" | "alreadyChosen" | "firstAllowed";

export type ProcedureRecommendation = { procedure: ProcedureRule; reason: RecommendationReason };

export type RegulationConfirmation =
  | { kind: "private" }
  | { kind: "privatePartI" }
  | {
      kind: "public";
      classification: string | null;
      recommended: ProcedureRecommendation;
      alternatives: ProcedureRule[];
    };

const OPEN_PROCEDURES: ProcedureType[] = [ProcedureType.Open, ProcedureType.OpenTenderCompetition];

export const recommendProcedure = (
  procedures: ProcedureRule[],
  current: ProcedureType | null | undefined,
  reopened = false,
): ProcedureRecommendation | null => {
  const chosen = procedures.find((procedure) => procedure.procedureType === current);
  if (chosen) {
    const recommendedAsOpen = !reopened && OPEN_PROCEDURES.includes(chosen.procedureType);
    return { procedure: chosen, reason: recommendedAsOpen ? "open" : "alreadyChosen" };
  }
  const open = procedures.find((procedure) => OPEN_PROCEDURES.includes(procedure.procedureType));
  if (open) return { procedure: open, reason: "open" };
  return procedures[0] ? { procedure: procedures[0], reason: "firstAllowed" } : null;
};

export type ConfirmationCircumstances = {
  buyerRunsPublicProcurements: boolean;
  outsideProcurementAct: boolean;
  reopened: boolean;
};

const privateConfirmationFor = (
  rules: Extract<ProcurementRules, { kind: "private" }>,
  { buyerRunsPublicProcurements, outsideProcurementAct }: ConfirmationCircumstances,
): RegulationConfirmation | null => {
  if (!buyerRunsPublicProcurements || outsideProcurementAct) return { kind: "private" };
  return rules.part === FoaPart.PartI ? { kind: "privatePartI" } : null;
};

export const regulationConfirmationFor = (
  rules: ProcurementRules,
  regulationStatus: AreaStatus | undefined,
  currentProcedure: ProcedureType | null | undefined,
  circumstances: ConfirmationCircumstances,
): RegulationConfirmation | null => {
  if (rules.kind === "private") return privateConfirmationFor(rules, circumstances);
  if (rules.kind !== "public" || (regulationStatus === "done" && !circumstances.reopened)) return null;
  if (!rules.part || rules.missing.length > 0) return null;
  const recommended = recommendProcedure(rules.procedures, currentProcedure, circumstances.reopened);
  if (!recommended) return null;
  return {
    kind: "public",
    classification: rules.classification,
    recommended,
    alternatives: rules.procedures.filter((procedure) => procedure !== recommended.procedure),
  };
};
